/**
 * Cohort retention page.
 *
 * Heatmap grid from GET /api/revenue/retention: rows are cohorts (period of
 * first-ever activity), columns are periods since the cohort, cells are the
 * share still active. Cell intensity uses the theme's color-mix convention;
 * a null cell (period not yet observable) gets no fill, never a zero.
 *
 * The semantics footnote mirrors packages/revenue/src/retention.ts — they must
 * not drift.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api,
  ApiFailure,
  type RetentionEntityName,
  type RetentionGrainName,
  type RetentionGridData,
  type RevenueWebsite,
} from '../api';
import { Notice } from './Panels';

export function Retention() {
  const [websites, setWebsites] = useState<RevenueWebsite[]>([]);
  const [websiteId, setWebsiteId] = useState<string>('');
  const [entity, setEntity] = useState<RetentionEntityName>('customers');
  const [grain, setGrain] = useState<RetentionGrainName>('week');
  const [dateRange, setDateRange] = useState('90d');
  const [grid, setGrid] = useState<RetentionGridData | null>(null);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  useEffect(() => {
    api.listRevenueWebsites()
      .then((data) => setWebsites(data.websites))
      .catch(() => setWebsites([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    try {
      const endDate = new Date().toISOString().slice(0, 10);
      const days = dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : dateRange === '180d' ? 180 : 365;
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      const response = await api.getRevenueRetention({
        startDate,
        endDate,
        grain,
        entity,
        ...(entity === 'visitors' && websiteId !== '' ? { websiteId } : {}),
      });
      setGrid(response.retention);
    } catch (error) {
      setGrid(null);
      setFailure(error instanceof ApiFailure ? error : new ApiFailure('Request failed.', 'Try again.', 0));
    } finally {
      setLoading(false);
    }
  }, [entity, grain, dateRange, websiteId]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="page">
      <header className="intro">
        <h2>Retention</h2>
        <p>Of the customers or visitors first seen in one period, how many were still active in each later period.</p>
      </header>

      <div className="controls">
        <label className="field">
          <span>Who</span>
          <select value={entity} onChange={(e) => setEntity(e.target.value as RetentionEntityName)}>
            <option value="customers">Customers (by first transaction)</option>
            <option value="visitors">Visitors (by first event)</option>
          </select>
        </label>
        <label className="field">
          <span>Period</span>
          <select value={grain} onChange={(e) => setGrain(e.target.value as RetentionGrainName)}>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </label>
        <label className="field">
          <span>Window</span>
          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="180d">Last 6 months</option>
            <option value="12m">Last 12 months</option>
          </select>
        </label>
        {entity === 'visitors' && (
          <label className="field">
            <span>Website</span>
            <select value={websiteId} onChange={(e) => setWebsiteId(e.target.value)}>
              <option value="">All websites</option>
              {websites.map((w) => (
                <option key={w.id} value={w.id}>{w.domain}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {failure !== null && <Notice tone="error" message={failure.message} remedy={failure.remedy} />}

      {loading && <div className="card"><p className="muted"><span className="spinner" /> Computing retention…</p></div>}

      {!loading && grid !== null && grid.cohorts.length === 0 && (
        <div className="card empty">
          <h3>No cohorts in this window</h3>
          <p>
            {grid.entity === 'customers'
              ? 'No customer made their first transaction in this period. Widen the window, record transactions first, or import a sales CSV on the Revenue page.'
              : 'No visitor had their first tracked event in this period. Widen the window, check that the tracking snippet is installed, or import a CSV on the Revenue page.'}
          </p>
        </div>
      )}

      {!loading && grid !== null && grid.cohorts.length > 0 && (
        <section className="card" style={{ overflowX: 'auto' }}>
          <header><h2>{grid.entity === 'customers' ? 'Customer' : 'Visitor'} cohorts, {grid.grain}ly</h2></header>
          <table className="data-table" aria-label={`${grid.entity} retention by ${grid.grain}ly cohort`}>
            <thead>
              <tr>
                <th>Cohort</th>
                <th style={{ textAlign: 'right' }}>Size</th>
                {grid.periodKeys.map((_, index) => (
                  <th key={index} style={{ textAlign: 'right' }} title={`${index} ${grid.grain}${index === 1 ? '' : 's'} after the cohort ${grid.grain}`}>
                    {index}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.cohorts.map((cohort) => (
                <tr key={cohort.key}>
                  <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{cohort.key}</td>
                  <td style={{ textAlign: 'right' }}>{cohort.size.toLocaleString('en-US')}</td>
                  {cohort.cells.map((cell, index) => (
                    <td
                      key={index}
                      title={cell === null
                        ? `${cohort.key}, period ${index}: not yet observable in this window`
                        : `${cohort.key}, period ${index}: ${cell.active} of ${cohort.size} active (${cell.rate.toFixed(1)}%)`}
                      aria-label={cell === null
                        ? `${cohort.key}, period ${index}: not yet observable`
                        : `${cohort.key}, period ${index}: ${cell.active} of ${cohort.size} active, ${cell.rate.toFixed(1)} percent`}
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        ...(cell === null
                          ? { color: 'var(--ink-muted)' }
                          : { background: `color-mix(in srgb, var(--series-1) ${Math.round(6 + cell.rate * 0.55)}%, var(--surface))` }),
                      }}
                    >
                      {cell === null ? '·' : `${cell.rate.toFixed(0)}%`}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <p className="muted" style={{ marginTop: 16, marginBottom: 0, fontSize: 12 }}>
            {grid.entity === 'customers'
              ? 'A customer is “active” in a period when they had at least one transaction in it; their cohort is the period of their first-ever transaction. Customer cohorts are workspace-wide.'
              : 'A visitor is “active” in a period when they had at least one tracked event in it; their cohort is the period of their first-ever event.'}
            {' '}Periods are UTC {grid.grain === 'week' ? 'ISO weeks starting Monday' : 'calendar months'}.
            A “·” cell is not yet observable — the window ends before that period does — which is not a 0%.
          </p>
        </section>
      )}
    </div>
  );
}
